import dayjs from "dayjs";
import { isObject } from "es-toolkit/compat";
import { CURRENT_USER_FILTER_ID } from "components/Bar/Filters/utils";
import { ApiColumnCondition, User } from "types/apiTypes";
import { LookupEntry, TableColumnType, TableFilterType, TableViewType, TabRecordIDSource } from "types/baTypes";
import { FilterValueType, RecordItem, SelectOption } from "types/common";
import { isColumnTextWithOptions } from "./columnUtils";
import {
  APP_FILTER_TYPES,
  CELL_TYPES_WITH_FILES,
  CellType,
  FILTER_BY_COLUMN_TYPES,
  FILTER_OPERATOR,
  FILTER_OPERATOR_NEED_VALUE,
  FILTER_OPERATOR_SEARCH_PARAMS,
  IS_DELETED_PAGE_FILTER,
  LookupTypes,
  NON_TEXT_DB_COLUMN_TYPES,
  SelectCellTypes,
  USER_TABLE_NAME
} from "./constants";
import { isColumnTagsJoinTable } from "./dataUtils";
import { ExtendedSchema } from "./schema";

export enum DATE_PRESETS {
  LAST_ONE_WEEK = "last_one_week",
  LAST_ONE_MONTH = "last_one_month",
  LAST_THREE_MONTHS = "last_three_months",
  LAST_SIX_MONTHS = "last_six_months",
  LAST_TWELVE_MONTHS = "last_twelve_months",
  LAST_TWENTY_FOUR_MONTHS = "last_twenty_four_months"
}

export const DATE_PRESET_FILTER_OPTIONS = [
  {
    title: "Last 1 week",
    value: DATE_PRESETS.LAST_ONE_WEEK
  },
  {
    title: "Last 1 month",
    value: DATE_PRESETS.LAST_ONE_MONTH
  },
  {
    title: "Last 3 months",
    value: DATE_PRESETS.LAST_THREE_MONTHS
  },
  {
    title: "Last 6 months",
    value: DATE_PRESETS.LAST_SIX_MONTHS
  },
  {
    title: "Last 12 months",
    value: DATE_PRESETS.LAST_TWELVE_MONTHS
  },
  {
    title: "Last 24 months",
    value: DATE_PRESETS.LAST_TWENTY_FOUR_MONTHS
  }
];

export enum SCORE_PRESETS {
  NINETY_PLUS = "90_plus",
  EIGHTY_PLUS = "80_plus",
  SEVENTY_PLUS = "70_plus",
  SIXTY_PLUS = "60_plus",
  FIFTY_PLUS = "50_plus"
}

export const SCORE_PRESET_FILTER_OPTIONS = [
  {
    title: "90+",
    value: SCORE_PRESETS.NINETY_PLUS
  },
  {
    title: "80+",
    value: SCORE_PRESETS.EIGHTY_PLUS
  },
  {
    title: "70+",
    value: SCORE_PRESETS.SEVENTY_PLUS
  },
  {
    title: "60+",
    value: SCORE_PRESETS.SIXTY_PLUS
  },
  {
    title: "50+",
    value: SCORE_PRESETS.FIFTY_PLUS
  }
];

export const getPresetFilterDateValues = (presetFilterOption: SelectOption) => {
  const presetValue = presetFilterOption.value;
  switch (presetValue) {
    case DATE_PRESETS.LAST_ONE_WEEK:
      return dayjs(new Date()).subtract(1, "week").toDate();
    case DATE_PRESETS.LAST_ONE_MONTH:
      return dayjs(new Date()).subtract(1, "month").toDate();
    case DATE_PRESETS.LAST_THREE_MONTHS:
      return dayjs(new Date()).subtract(3, "month").toDate();
    case DATE_PRESETS.LAST_SIX_MONTHS:
      return dayjs(new Date()).subtract(6, "month").toDate();
    case DATE_PRESETS.LAST_TWELVE_MONTHS:
      return dayjs(new Date()).subtract(12, "month").toDate();
    case DATE_PRESETS.LAST_TWENTY_FOUR_MONTHS:
      return dayjs(new Date()).subtract(24, "month").toDate();
    default:
      return null;
  }
};

export const getPresetFilterScoreValues = (presetFilterOption: SelectOption) => {
  const presetValue = presetFilterOption.value;
  switch (presetValue) {
    case SCORE_PRESETS.NINETY_PLUS:
      return 90;
    case SCORE_PRESETS.EIGHTY_PLUS:
      return 80;
    case SCORE_PRESETS.SEVENTY_PLUS:
      return 70;
    case SCORE_PRESETS.SIXTY_PLUS:
      return 60;
    case SCORE_PRESETS.FIFTY_PLUS:
      return 50;
    default:
      return undefined;
  }
};

export const getFilterFieldPathForRecordId = (recordIdSource: TabRecordIDSource) => {
  if (recordIdSource?.columnName) {
    return recordIdSource.columnName;
  }
  let finalPath = "";
  if (recordIdSource?.columnRecordLookupPath) {
    Object.keys(recordIdSource.columnRecordLookupPath).forEach((level: string) => {
      const lookupDetails = recordIdSource.columnRecordLookupPath?.[level];
      if (lookupDetails?.lookupColumns?.length) {
        finalPath += lookupDetails.lookupTableName + "." + lookupDetails.lookupColumns[0];
      } else {
        finalPath += `${level === "0" ? "" : "."}` + level;
      }
    });
  }
  return finalPath;
};

export const constructLookupFilter = ({
  lookupPath,
  type,
  columnHeader,
  isFilterColumnImageRecordType,
  filterOperator
}: {
  lookupPath: { [lookupLevel: string]: LookupEntry };
  type: CellType;
  useColumnLabel?: boolean;
  columnHeader?: string;
  isFilterColumnImageRecordType?: boolean;
  filterOperator?: FILTER_OPERATOR;
}) => {
  let finalFilter = "";
  let finalLookupColumn = "";
  const finalLookupPath = { ...lookupPath };

  if (isFilterColumnImageRecordType) {
    const isLookupLvl2RecordType = lookupPath?.["0"]?.isLvl2RecordTypeForeignKey;
    if (isLookupLvl2RecordType) {
      finalFilter =
        lookupPath?.["0"]?.lookupColumnLabel ||
        lookupPath?.["0"]?.lookupForeignKey ||
        lookupPath?.["0"]?.lookupTableName;
      if (lookupPath?.["1"]?.lookupForeignKey) {
        finalFilter += ".";
        finalFilter += lookupPath?.["1"]?.lookupForeignKey;
      }
    } else {
      if (lookupPath?.[0]?.lookupType === LookupTypes.FOREIGN) {
        const { lookupTableName, lookupForeignKey, lookupColumnLabel } = lookupPath[0];
        finalFilter = lookupColumnLabel || lookupForeignKey || lookupTableName;
      } else if (lookupPath?.[0]?.lookupType === LookupTypes.JOIN) {
        const { lookupTableName, lookupForeignKey, lookupColumnLabel } = lookupPath[1];
        finalFilter = lookupColumnLabel || lookupForeignKey || lookupTableName;
      }
    }
    return {
      finalFilter,
      finalLookupColumn,
      isPeopleForeignKey: isLookupLvl2RecordType && finalFilter?.startsWith("image_People"), // ##HARDCODED check for people image as column type is File
      isForeignKey: lookupPath?.[0]?.lookupType === LookupTypes.FOREIGN || isLookupLvl2RecordType
    };
  }
  if (
    lookupPath &&
    CELL_TYPES_WITH_FILES.includes(type) &&
    !(lookupPath?.["0"]?.lookupColumnLabel || "").startsWith("image_")
  ) {
    // Fix lookup path to remove files
    let fileLevelFound = false;
    Object.keys(lookupPath).forEach((lookupLevel) => {
      if (fileLevelFound) {
        delete finalLookupPath?.[lookupLevel];
        fileLevelFound = true;
      }
    });
  }
  if (lookupPath && type === CellType.DEAL) {
    // Handle project separately to link to places id
    // Fix lookup path to remove files
    let dealLevelFound = false;
    Object.keys(lookupPath).forEach((lookupLevel) => {
      const { lookupTableName = "" } = lookupPath?.[lookupLevel] || {};
      if (dealLevelFound) {
        delete finalLookupPath?.[lookupLevel];
      }
      if (lookupTableName === "deals") {
        dealLevelFound = true;
      }
    });
  }

  let isPeopleForeignKey = false;
  const firstLevel = finalLookupPath?.["0"];
  if (
    lookupPath &&
    type === CellType.PEOPLE &&
    !!columnHeader &&
    !["Created By", "Updated By"].includes(columnHeader) // ##HARDCODED Exclude these columns as we pick id from selected filter option, not uuid
  ) {
    if (
      !!firstLevel?.lookupForeignKey &&
      firstLevel?.lookupTableName === USER_TABLE_NAME &&
      !["created_by", "updated_by"].includes(firstLevel?.lookupForeignKey) // Exclude created by and updated by from foreign tables as well
    ) {
      // Directly use this field
      finalFilter = firstLevel?.lookupForeignKey;
      isPeopleForeignKey = true;
    }
  }
  if (finalLookupPath && filterOperator === FILTER_OPERATOR.EMPTY) {
    // Join tables with empty filter use the join table name directly
    finalFilter = firstLevel?.lookupColumnLabel || firstLevel?.lookupForeignKey || firstLevel?.lookupTableName;
  } else if (!isPeopleForeignKey) {
    Object.keys(finalLookupPath || {}).forEach((lookupLevel, keyIdx) => {
      const { lookupTableName, lookupForeignKey, lookupColumnLabel, lookupColumns, lookupDisplayColumn, lookupType } =
        finalLookupPath[lookupLevel];

      // for deals lookup using foreignKey and when at deals level use the foreignKey first if present
      if (lookupLevel !== "0" && lookupTableName === "deals" && type === CellType.DEAL) {
        finalFilter += `${lookupForeignKey || lookupColumnLabel || lookupTableName}`;
      } else {
        if (lookupType === LookupTypes.FOREIGN_EXTERNAL) {
          // Don't use lookupForeignKey in this case, as the table name is used directly
          finalFilter += `${lookupColumnLabel || lookupTableName}`;
        } else {
          finalFilter += `${lookupColumnLabel || lookupForeignKey || lookupTableName}`;
        }
      }
      if (keyIdx !== Object.keys(finalLookupPath).length - 1) {
        finalFilter += ".";
      } else {
        finalFilter += ".";
        finalLookupColumn = lookupDisplayColumn || lookupColumns?.[0] || "";
      }
    });
  }

  return {
    finalFilter: finalFilter.endsWith(".") ? finalFilter.slice(0, -1) : finalFilter,
    isPeopleForeignKey,
    finalLookupColumn,
    isForeignKey: !!finalLookupPath?.["0"]?.lookupForeignKey
  };
};

// filterString:  Returns the raw PostgREST syntax for the filter names and values
// orOptions: Return or options such as foreignTables
export const constructORFilterFromGroup = (
  filterGroup: TableFilterType[],
  filterId?: string,
  isBooleanFilter = false,
  isNumericFilter = false,
  currentUser?: User
) => {
  if (!filterGroup?.length) {
    return;
  }
  let filterString = "";
  const orOptions: any = {};
  filterGroup.forEach((filter: TableFilterType) => {
    const {
      filterValue,
      filterOperator,
      filterField,
      filterLookupPath,
      isFilterTextSearch,
      useLikeOperator,
      filterDbType
    } = filter;
    if (filterLookupPath) {
      let mainLookupTableName = "";

      // in case of current user filter and people cell
      if (filterId === CURRENT_USER_FILTER_ID && filter.column?.type === CellType.PEOPLE) {
        filterString += filterField || "";
      } else {
        Object.keys(filterLookupPath || {}).forEach((level: string) => {
          const lookupDetails = filterLookupPath?.[level];
          if (level === "0") {
            orOptions.foreignTable = lookupDetails.lookupTableName;
            mainLookupTableName = lookupDetails.lookupTableName;
          }

          if (lookupDetails?.lookupColumns?.length) {
            filterString +=
              (mainLookupTableName !== lookupDetails.lookupTableName ? lookupDetails.lookupTableName + "." : "") +
              lookupDetails.lookupColumns[0];
          } else {
            filterString +=
              (mainLookupTableName !== lookupDetails.lookupTableName ? lookupDetails.lookupTableName : "") + ".";
          }
        });
      }
    } else {
      filterString += filterField || "";
    }

    const filterFld = filterString;
    switch (filterOperator) {
      case FILTER_OPERATOR.EQUALS:
      case FILTER_OPERATOR.CURRENT_RECORD:
      case FILTER_OPERATOR.CURRENT_DEAL:
        filterString += `.eq.${filterValue}`;
        break;
      case FILTER_OPERATOR.NOT_EQUALS:
        filterString += `.neq.${filterValue}`;
        break;
      case FILTER_OPERATOR.GREATER_THAN:
        filterString += `.gt.${filterValue}`;
        break;
      case FILTER_OPERATOR.GREATER_THAN_EQUALS:
        filterString += `.gte.${filterValue}`;
        break;
      case FILTER_OPERATOR.LESS_THAN:
        filterString += `.lt.${filterValue}`;
        break;
      case FILTER_OPERATOR.LESS_THAN_EQUALS:
        filterString += `.lte.${filterValue}`;
        break;
      case FILTER_OPERATOR.EMPTY:
        filterString += `.is.null`; // Null check
        if (!isBooleanFilter && !isNumericFilter) {
          filterString += `,${filterFld}.eq.`; //  empty string check
        }
        break;
      case FILTER_OPERATOR.NOT_EMPTY:
        filterString += `not.is.null`; // not Null check
        if (!isBooleanFilter && !isNumericFilter) {
          filterString += `,${filterFld}.neq.`; // not empty string check
        }
        break;
      case FILTER_OPERATOR.CONTAINS:
        if (useLikeOperator) {
          filterString += `.ilike.%${filter.skipEncoding ? filterValue : encodeURIComponent(filterValue as string)}%`;
        } else if (isFilterTextSearch) {
          filterString += `.fts.${filter.skipEncoding ? filterValue : encodeURIComponent(filterValue as string)}`;
        }
        break;
      case FILTER_OPERATOR.CURRENT_USER:
        const finalValue = filterDbType === "bigint" ? currentUser?.id : currentUser?.user_id;
        filterString += `.eq.${finalValue}`;
        break;
    }
    filterString += ",";
  });
  if (filterString.endsWith(",")) {
    filterString = filterString.slice(0, -1);
  }
  return { filterString, orOptions };
};

export const checkTableFiltersValidation = (tableFiltersOption?: TableViewType) => {
  if (!tableFiltersOption) {
    return true;
  }
  const { filters } = tableFiltersOption;
  if (!filters) {
    return true;
  }
  return filters.every(
    (filter) =>
      !FILTER_OPERATOR_NEED_VALUE.includes(filter.filterOperator as FILTER_OPERATOR) ||
      (FILTER_OPERATOR_NEED_VALUE.includes(filter.filterOperator as FILTER_OPERATOR) &&
        filter.filterValue !== undefined &&
        filter.filterValue !== null &&
        filter.filterValue !== "")
  );
};

// Merges filters from different columns into a single filter
// if they are to the same table
export const constructMultiFilter = (filtersMap: { [key: string]: TableFilterType[] }) => {
  let finalFilters: TableFilterType[] = [];
  if (Object.keys(filtersMap).length === 0) {
    return finalFilters;
  }
  Object.keys(filtersMap).forEach((key) => {
    if (key === "single") {
      finalFilters = [...finalFilters, ...filtersMap[key]];
    } else {
      const tableFilters = filtersMap[key];
      if (tableFilters.length > 1) {
        // Check if all filters are for equals
        const inValues: any = [];
        tableFilters.forEach((filter) => {
          if (
            filter.column &&
            filter.column?.lookupPath &&
            (!filter.filterOperator ||
              filter.filterOperator === FILTER_OPERATOR.EQUALS ||
              filter.filterOperator === FILTER_OPERATOR.IN)
          ) {
            if (!Array.isArray(filter.filterValue)) {
              inValues.push(filter.filterValue);
            } else {
              inValues.push(...filter.filterValue);
            }
          } else {
            finalFilters.push(filter);
          }
        });
        if (inValues?.length) {
          finalFilters.push({
            column: tableFilters[0].column,
            filterOperator: FILTER_OPERATOR.IN,
            filterValue: [...inValues],
            isFilterColumnImageRecordType: tableFilters[0].isFilterColumnImageRecordType
          });
        }
      } else {
        finalFilters.push(tableFilters[0]);
      }
    }
  });
  return finalFilters;
};

// Returns true if column is single level lookup with a foreign key
export const isColumnSingleLevelFKLookup = (column: TableColumnType) => {
  if (!column?.isLookup) return false;

  const { lookupPath } = column;
  const lookupLevels = Object.keys(lookupPath || {});
  if (lookupLevels.length !== 1) return false;
  return !!lookupPath?.[lookupLevels[0]]?.lookupForeignKey;
};

// Special hard coded case to check for file type lookup
export const isColumnFileType = (column: TableColumnType) => {
  return (
    column.type === CellType.BADGE &&
    ((column?.isLookup &&
      column?.lookupPath?.[0]?.lookupForeignKey === "file_id" &&
      Object.keys(column?.lookupPath || {}).length === 1) ||
      column?.name === "file_type")
  );
};

// Returns filter value for a filter
// Only used for view level filters not page filters
export const getSupabaseFilterValue = (filter: TableFilterType) => {
  let finalFilterValue = filter.filterValue;
  const { filterValue, filterOperator } = filter;
  if (filter.column?.id && filter.column?.isLookup && !filter?.column?.isTextArray) {
    const { column } = filter;
    if (Array.isArray(filterValue) && filterValue.length) {
      finalFilterValue =
        filterOperator === FILTER_OPERATOR.IN
          ? filterValue.map((fItem: RecordItem) => {
              if (column.type === CellType.DEAL && fItem?.record?.projectId) {
                return fItem?.record?.projectId;
              }
              return fItem?.record?.id;
            })
          : filter.isViewFilter
            ? (filterValue as RecordItem[])?.[0]?.title || ""
            : column.type === CellType.DEAL && (filterValue as RecordItem[])?.[0]?.record?.projectId
              ? (filterValue as RecordItem[])?.[0]?.record?.projectId
              : (filterValue as RecordItem[])?.[0]?.record?.id;
    } else {
      finalFilterValue =
        // For badge cell types we want the text value
        filter.isViewFilter || filter?.column?.type === CellType.BADGE || filter.useFilterLookupColumn
          ? !isObject(filterValue)
            ? filterValue
            : (filterValue as RecordItem)?.title || ""
          : (filterValue as RecordItem)?.record?.id || filterValue;
      if (column.type === CellType.DEAL && (filterValue as RecordItem)?.record?.projectId) {
        finalFilterValue = (filterValue as RecordItem)?.record?.projectId;
      }
    }
  } else if (
    (filterOperator === FILTER_OPERATOR.IN || filterOperator === FILTER_OPERATOR.NOT_IN) &&
    typeof filterValue === "string"
  ) {
    finalFilterValue = filterValue.split(",").filter((f) => f.trim());
  } else if (filter.column?.id && isColumnTextWithOptions(filter.column)) {
    if (Array.isArray(filterValue) && filterValue.length) {
      finalFilterValue = filter.column?.isTextArray
        ? filterValue
        : filterOperator === FILTER_OPERATOR.IN
          ? filterValue.map((fItem: RecordItem) =>
              filter.isViewFilter || filter.column?.columnEnumOptions?.length ? fItem.title : fItem?.record?.id
            )
          : filter.isViewFilter || filter.column?.isTextArray || filter.column?.columnEnumOptions?.length
            ? (filterValue as RecordItem[])?.[0]?.title
            : (filterValue as RecordItem[])?.[0]?.record?.id;
    } else {
      finalFilterValue = filter.column?.isTextArray
        ? (filterValue as string)?.includes(",")
          ? (filterValue as string).split(",")
          : filterValue
            ? [filterValue]
            : ""
        : filter.isViewFilter
          ? (filterValue as RecordItem)?.title || filterValue
          : (filterValue as RecordItem)?.value || filterValue;
    }
  }

  return finalFilterValue;
};

export const getForeignKeyForFilterType = (filterType: APP_FILTER_TYPES) => {
  switch (filterType) {
    case APP_FILTER_TYPES.LOOKUP_PATH:
      return "lookup_filter_id";
    case APP_FILTER_TYPES.COLUMN_OPTIONS:
      return "column_options_filter_id";
    case APP_FILTER_TYPES.ADD_TAB_FILTER:
      return "add_page_filter_id";
    case APP_FILTER_TYPES.ADD_FILTER:
      return "add_filter_id";
    case APP_FILTER_TYPES.PAGE_FILTER:
      return "page_filters_id";
    case APP_FILTER_TYPES.SECTION_FILTER:
      return "ui_filters_id";
    case APP_FILTER_TYPES.PAGE_TAB_FILTER:
      return "tab_filter_id";
    default:
      return "";
  }
};

export const getTableNameForFilters = ({
  pageColumnId,
  viewColumnId,
  tabId,
  pageId,
  sectionId
}: {
  pageColumnId?: string;
  tabId?: string;
  pageId?: string;
  sectionId?: string;
  viewColumnId?: string;
}) => {
  if (pageColumnId) {
    return "ui_pages_columns";
  } else if (tabId) {
    return "ui_tabs";
  } else if (pageId) {
    return "pages";
  } else if (sectionId) {
    return "ui_sections";
  } else if (viewColumnId) {
    return "ui_views_columns";
  }
  return "";
};

export const getFilterValueAndRelatedProps = (
  filter: TableFilterType,
  value: FilterValueType,
  extendedSchema?: ExtendedSchema
) => {
  const updatedFilter = filter.column ? getFilterSpecialProps(filter, filter.column, extendedSchema) : { ...filter };
  const filterVal = getSupabaseFilterValue({
    ...updatedFilter,
    filterValue: value
  });

  return { ...updatedFilter, filterValue: filterVal };
};

export const getFilterValueFromDbFilterValue = ({
  filterColumn,
  filterValue
}: {
  filterColumn?: TableColumnType;
  extendedSchema?: ExtendedSchema;
  filterValue?: string;
}) => {
  if (filterColumn) {
    let finalFilterValue: any = filterValue ?? "";
    if (filterColumn?.isLookup) {
      if ((SelectCellTypes.includes(filterColumn.type) || filterColumn.type === CellType.BADGE) && finalFilterValue) {
        if (filterColumn.isMultiple) {
          finalFilterValue = finalFilterValue.split(",").map((fItem: string) => ({
            value: fItem,
            title: fItem,
            record: {
              id: fItem
            }
          }));
        } else {
          finalFilterValue = {
            value: finalFilterValue,
            title: finalFilterValue,
            record: {
              id: finalFilterValue
            }
          };
        }
      }
    }
    if (filterColumn?.dbType?.format === "boolean" || filterColumn.type === CellType.BOOLEAN) {
      finalFilterValue = filterValue === "true";
    }
    if (filterColumn?.isTextArray && filterValue) {
      finalFilterValue = filterValue.split(",");
    }
    return finalFilterValue;
  }
  return filterValue;
};

// If true filter cannot use emtpy string in value
export const isFilterNonTextType = (filter: TableFilterType) => {
  return (
    NON_TEXT_DB_COLUMN_TYPES.includes(filter.column?.dbType?.format || "") ||
    NON_TEXT_DB_COLUMN_TYPES.includes(filter.filterDbType || "") ||
    filter.column?.type === CellType.DATETIME ||
    filter.column?.type === CellType.BOOLEAN
  );
};

export const getFinalTableFilterOptions = (tableFiltersOption: { filters: TableFilterType[] }) => {
  const filters = [];
  if (tableFiltersOption?.filters?.length) {
    let hasIsDeletedFilter = false;
    filters.push(
      ...tableFiltersOption?.filters.filter((filter) => {
        if (filter?.filterField === "is_deleted") {
          hasIsDeletedFilter = true;
        }
        return true;
      })
    );
    if (!hasIsDeletedFilter) {
      filters.push(IS_DELETED_PAGE_FILTER);
    }
  }

  return filters;
};

// This is the final method to add filter related special props that control the final filter processing
export const getFilterSpecialProps = (
  filter: TableFilterType,
  filterCol: TableColumnType,
  extendedSchema?: ExtendedSchema
) => {
  let isFilterColumnJoinTable = false;
  let useFilterLookupColumn = false;
  let isTagsJoinTable = false;
  if (filterCol?.isLookup) {
    if (
      isColumnFileType(filterCol) ||
      (filterCol?.dbType?.format === "integer" && filterCol?.type === CellType.TEXT) ||
      filterCol.type === CellType.BADGE
    ) {
      useFilterLookupColumn = true;
    }
    isTagsJoinTable = isColumnTagsJoinTable(filterCol, extendedSchema);
    // Check if column type is in FILTER_BY_COLUMN_TYPES if so we should compare lookup column instead of id
    // Second condition is for lookup column, which has Select options and is of type Text
    // This may be expanded to include other conditions in the future
    if (
      FILTER_BY_COLUMN_TYPES.includes(filterCol?.type) ||
      (filterCol?.type === CellType.TEXT && filterCol?.isSelect && filterCol?.isLookup)
    ) {
      useFilterLookupColumn = true;
    }
    const tableName = filterCol?.lookupPath?.[0]?.lookupTableName;
    if (tableName && extendedSchema?.[tableName]?.compositePk?.length) {
      isFilterColumnJoinTable = true;
    }
  }
  return {
    ...filter,
    isFilterColumnJoinTable,
    innerAtAllLevels: isTagsJoinTable || !!isFilterColumnJoinTable || !!filterCol.cellConfig?.hasPresetFilter,
    excludeFromInner: filter?.filterOperator === FILTER_OPERATOR.EMPTY,
    column: { ...filterCol },
    useFilterLookupColumn,
    isFromFilters: true,
    useColumnLabel: filterCol.type === CellType.PEOPLE || isTagsJoinTable
  };
};

export const getFinalFilterForColAndValue = (
  filterCol: TableColumnType,
  filterOperator: string,
  initialFilterValue?: any,
  filterNameVal?: string
) => {
  let filterValue: any = initialFilterValue ?? "";
  const matchedOperatorKey = Object.keys(FILTER_OPERATOR_SEARCH_PARAMS).find(
    (filterOp) =>
      filterOperator === FILTER_OPERATOR_SEARCH_PARAMS[filterOp as keyof typeof FILTER_OPERATOR_SEARCH_PARAMS]
  );
  if (matchedOperatorKey) {
    filterOperator = FILTER_OPERATOR[matchedOperatorKey as keyof typeof FILTER_OPERATOR];
  }
  const isColTextArray = !!filterCol?.isTextArray;

  if (filterCol?.isLookup) {
    filterValue = getFilterValueFromDbFilterValue({ filterColumn: filterCol, filterValue });
  }
  if (isColTextArray && !filterCol?.isLookup) {
    // filterValue is an array if isLookup is true, as it's processed already in getFilterValueFromDbFilterValue
    filterValue = typeof filterValue === "string" && filterValue ? filterValue.split(",") : "";
  }
  return {
    ...filterCol,
    filterField: filterCol.name || filterNameVal?.replace("f_", ""),
    filterOperator,
    filterValue,
    column: { ...filterCol }
  };
};

export const getApiColumnConditionForFilter = ({
  filter,
  workspaceId,
  columnFiltersId,
  additionalFields
}: {
  filter: TableFilterType;
  workspaceId?: string;
  columnFiltersId: string;
  additionalFields?: Partial<ApiColumnCondition>;
}) => ({
  filter_lookup_path: filter?.filterLookupPath,
  field: filter?.filterField,
  operator: filter?.filterOperator,
  value: filter?.filterValue,
  column_filters_id: columnFiltersId,
  record_id_source: filter.recordIdSource,
  include_null: filter?.includeNull,
  is_parent_synced: filter?.isParentSynced,
  is_user_visible: filter?.isUserVisible,
  ui_page_column_id: filter?.pageColumnId,
  option_value: filter.filterOptionValue,
  filter_db_type: filter.filterDbType,
  workspace_id: workspaceId,
  group_condition_id: filter.groupConditionId,
  ...additionalFields
});

export const CELL_WITH_PRESET_FILTERS = [CellType.DATETIME, CellType.DATE, CellType.SCORE];

// This function checks if the combination of operator or selected operator is compatible with multiple values
// It assumed the first is not lookup and expects a filterField to be present
// When added check for lookup filter, please update this same function
// Returns an error message if the combination is not compatible
export const getBasicFilterMultipleValuesErrorMessage = (
  existingFilter: TableFilterType,
  finalProps: Partial<TableFilterType>
) => {
  // Existing filter is equals and incoming filter is also not correct for multiple values
  if (
    ([FILTER_OPERATOR.EQUALS, FILTER_OPERATOR.NOT_EQUALS].includes(existingFilter.filterOperator as FILTER_OPERATOR) &&
      !finalProps.filterOperator) ||
    (!!finalProps.filterOperator &&
      ![FILTER_OPERATOR.CONTAINS, FILTER_OPERATOR.IN].includes(finalProps.filterOperator as FILTER_OPERATOR))
  ) {
    return `If using , to indicate multiple values, please use CONTAINS OR IN operator. 
      The current operator will search for the full text value as is.`;
  }

  return null;
};
